import { CourseResponseDto } from '@repo/types';
import { COURSE_STATE, PRODUCT_KIND, PRODUCT_PACKAGE_KIND } from '@theailab-dev/parchment';
import type { ProductKind, ProductPackageKind } from '@theailab-dev/parchment';
import { useState, useEffect } from 'react';
import { ProductItemDto } from '../api/productApi';
import { getErrorMessage } from '../utils/errorHandler';

// CourseResponseDto에 price 속성이 포함된 타입
export type CourseWithPrice = CourseResponseDto & { price?: number | null };

interface UseProductItemFormProps {
  initialItem?: ProductItemDto | null;
  productKind: ProductKind;
  existingItems?: ProductItemDto[];
  onSubmit: (data: {
    courseId?: number | null;
    originalPrice: number;
    discount: number;
    packageKind: ProductPackageKind;
  }) => Promise<void>;
  onClose: () => void;
  isOpen: boolean;
  /**
   * 컴포넌트별 커스텀 유효성 검사 함수
   * - false를 반환하면 제출이 중단됩니다.
   * - 예: 단일 상품의 아이템 개수 제한 등
   */
  customValidator?: () => Promise<boolean>;
}

/**
 * 상품 아이템 추가/수정 폼 로직을 관리하는 커스텀 훅
 *
 * @description
 * - 폼 상태 관리 (할인가, 패키지 유형 등)
 * - 가격 계산 로직 (원가, 할인가 비교)
 * - 유효성 검사 (필수값, 가격 범위, 패키지 유형 규칙)
 * - 제출 처리
 */
export const useProductItemForm = ({
  initialItem,
  productKind,
  existingItems = [],
  onSubmit,
  onClose,
  isOpen,
  customValidator,
}: UseProductItemFormProps) => {
  const [discount, setDiscount] = useState('');
  const [selectedCourse, setSelectedCourse] = useState<CourseWithPrice | null>(null);
  const [packageKind, setPackageKind] = useState<ProductPackageKind>(PRODUCT_PACKAGE_KIND.REQUIREMENT);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [originalPrice, setOriginalPrice] = useState<number | null>(null);

  // 알림 모달 상태
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    type: 'error' | 'warning' | 'success' | 'info';
    title: string;
    message: string;
  }>({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
  });

  // 초기값 설정
  useEffect(() => {
    if (initialItem) {
      setDiscount(initialItem.discount.toString());
      setPackageKind(initialItem.packageKind || PRODUCT_PACKAGE_KIND.REQUIREMENT);
      setOriginalPrice(initialItem.originalPrice);

      if (productKind === PRODUCT_KIND.SINGLE && initialItem.course) {
        setSelectedCourse({
          ...initialItem.course,
          price: initialItem.originalPrice, // 원가를 price로 설정
          // 필요한 다른 속성들 채우기...
          id: initialItem.courseId!,
          title: initialItem.course.title,
          level: initialItem.course.level,
          materialsUrl: initialItem.course.materialsUrl,
          isSpecial: initialItem.course.isSpecial ?? false,
          state: COURSE_STATE.NORMAL,
          kind: initialItem.course.kind,
          totalLessonCount: initialItem.course.totalLessonCount || 0,
          totalDuration: initialItem.course.totalDuration,
          preknowledge: null,
          startAt: null,
          endAt: null,
          createdAt: new Date(),
          updatedAt: new Date(),
          productItemId: initialItem.course.productItemId ?? null,
          category: initialItem.course.category
            ? {
                id: initialItem.course.category.id,
                title: initialItem.course.category.title,
                thumbnailUrl: null,
              }
            : null,
          instructor: initialItem.course.instructor,
        });
      }
    } else {
      // 초기화
      setDiscount('');
      setPackageKind(PRODUCT_PACKAGE_KIND.REQUIREMENT);
      setSelectedCourse(null);
      setOriginalPrice(null);
    }
  }, [initialItem, productKind, isOpen]);

  const showAlert = (type: 'error' | 'warning' | 'success' | 'info', title: string, message: string) => {
    setAlertModal({ isOpen: true, type, title, message });
  };

  const closeAlert = () => {
    setAlertModal((prev) => ({ ...prev, isOpen: false }));
  };

  const handleCourseSelect = (course: CourseResponseDto) => {
    // course에 price가 없으면 기존 originalPrice 유지
    const courseWithPrice: CourseWithPrice = {
      ...course,
      price: course.price ?? initialItem?.originalPrice ?? null,
    };
    setSelectedCourse(courseWithPrice);

    if (!initialItem) {
      // 추가 모드일 때만 가격 자동 설정
      // 1. 선택된 강의 정보에서 가격 확인 (우선순위)
      if (course.price !== undefined && course.price !== null) {
        setOriginalPrice(Number(course.price));
      } else {
        // 2. existingItems에서 찾기 (백업: 이미 등록된 아이템 정보 활용)
        const matchingItem = existingItems.find((item) => item.courseId === course.id);
        if (matchingItem) {
          setOriginalPrice(matchingItem.originalPrice);
        } else {
          setOriginalPrice(null);
        }
      }
    } else {
      // 수정 모드: 강의 변경 시 원가도 업데이트 (강의에 price가 있는 경우)
      if (course.price !== undefined && course.price !== null) {
        setOriginalPrice(Number(course.price));
      }
      // 강의에 price가 없으면 기존 originalPrice 유지
    }
  };

  const validateAndSubmit = async () => {
    // 1. 커스텀 유효성 검사 (컴포넌트별 특수 로직)
    if (customValidator) {
      const isValid = await customValidator();
      if (!isValid) return;
    }

    // 2. 기본 유효성 검사
    // 단일 유형인 경우 강의 선택 필수
    if (productKind === PRODUCT_KIND.SINGLE && !selectedCourse) {
      showAlert('error', '입력 오류', '연결할 강의를 선택해주세요.');
      return;
    }

    // 유효성 검증 (콤마 제거 후 숫자 변환)
    const discountValue = discount.trim() ? parseFloat(discount.replace(/,/g, '')) : 0;
    if (isNaN(discountValue) || discountValue < 0) {
      showAlert('error', '입력 오류', '할인가는 0 이상의 숫자로 입력해주세요.');
      return;
    }

    // 원가 결정
    const currentOriginalPrice =
      productKind === PRODUCT_KIND.SINGLE && selectedCourse ? selectedCourse.price || 0 : originalPrice || 0;

    // 할인가가 원가보다 크면 오류 (같으면 무료이므로 허용)
    if (discountValue > currentOriginalPrice) {
      showAlert(
        'error',
        '입력 오류',
        `할인가는 원가(${currentOriginalPrice.toLocaleString()}원)보다 작거나 같아야 합니다.\n현재 입력된 할인가: ${discountValue.toLocaleString()}원`,
      );
      return;
    }

    // REQUIREMENT(필수) 아이템 규칙 검증
    // - 전체 아이템 중 최소 1개는 REQUIREMENT여야 함
    const otherItems = existingItems.filter((i) => !initialItem || i.id !== initialItem.id);
    const otherRequirementCount = otherItems.filter(
      (i) => i.packageKind === PRODUCT_PACKAGE_KIND.REQUIREMENT || !i.packageKind,
    ).length;

    if (packageKind === PRODUCT_PACKAGE_KIND.OPTION && otherRequirementCount === 0) {
      showAlert(
        'error',
        '선택 불가',
        '필수(REQUIREMENT) 아이템이 최소 1개 이상 있어야 합니다.\n현재 다른 필수 아이템이 없으므로 이 아이템은 필수로 설정해야 합니다.',
      );
      return;
    }

    try {
      setIsSubmitting(true);
      await onSubmit({
        courseId: selectedCourse?.id,
        originalPrice: currentOriginalPrice,
        discount: discountValue,
        packageKind,
      });
      onClose();
    } catch (error) {
      console.error('저장 실패:', error);

      // 에러 메시지 추출 (errorHandler 유틸리티 사용)
      const errorMessage = getErrorMessage(error);

      showAlert('error', '저장 실패', errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    discount,
    setDiscount,
    selectedCourse,
    setSelectedCourse,
    packageKind,
    setPackageKind,
    isSubmitting,
    alertModal,
    showAlert,
    closeAlert,
    handleCourseSelect,
    validateAndSubmit,
    originalPrice,
    setOriginalPrice,
  };
};
